import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const postJson = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Login failed');
  return data;
};

const tryLogin = async (attempts) => {
  let lastError = null;

  for (const attempt of attempts) {
    try {
      return await attempt();
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error('Invalid credentials');
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const routeUser = async (data) => {
    if (data.student) {
      const student = data.student;
      if (student.forcePasswordChange) {
        navigate('/student/force-password-change', { state: { student } });
      } else {
        localStorage.setItem('studentUser', JSON.stringify(student));
        navigate('/student-dashboard', { state: { student } });
      }
      return;
    }

    if (data.parent) {
      if (data.parent.forcePasswordChange) {
        localStorage.removeItem('parentUser');
        localStorage.setItem('tempParentUser', JSON.stringify(data.parent));
        navigate('/parent/change-password', { state: { parent: data.parent } });
        return;
      }
      localStorage.removeItem('tempParentUser');
      localStorage.setItem('parentUser', JSON.stringify(data.parent));
      navigate('/parent/dashboard', { state: { parent: data.parent } });
      return;
    }

    if (data.userType === 'security') {
      localStorage.setItem('securityUser', JSON.stringify(data.user || data.admin));
      navigate('/security/dashboard');
      return;
    }

    if (data.userType === 'teacher' || data.admin?.role === 'teacher') {
      const teacher = data.admin;
      if (password === 'teacher123' && !teacher.passwordChanged) {
        localStorage.setItem('tempTeacherUser', JSON.stringify(teacher));
        navigate('/teacher/change-password');
      } else {
        localStorage.setItem('teacherUser', JSON.stringify(teacher));
        navigate('/teacher-dashboard');
      }
      return;
    }

    if (data.userType === 'staff' || data.admin?.role === 'staff') {
      let staff = data.admin;
      try {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staff.id}`);
        if (response.ok) {
          staff = { ...staff, ...(await response.json()), id: staff.id, email: staff.email, role: staff.role, name: staff.name };
        }
      } catch (err) {
        console.warn('Failed to fetch complete staff profile:', err);
      }

      if (password === 'staff123' && !staff.passwordChanged) {
        localStorage.setItem('tempStaffUser', JSON.stringify(staff));
        navigate('/staff/change-password');
      } else {
        localStorage.setItem('staffUser', JSON.stringify(staff));
        navigate('/staff-dashboard');
      }
      return;
    }

    localStorage.setItem('adminUser', JSON.stringify(data.admin));
    const returnTo = new URLSearchParams(window.location.search).get('returnTo');
    navigate(['/admin/cameras', '/admin/gallery-management', '/admin/news'].includes(returnTo) ? returnTo : '/dashboard');
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    const loginId = identifier.trim();
    const looksLikeEmail = loginId.includes('@');

    try {
      const attempts = looksLikeEmail
        ? [
            () => postJson(API_ENDPOINTS.ADMIN_LOGIN, { email: loginId, password }),
            () => postJson(API_ENDPOINTS.PARENT_LOGIN, { email: loginId, password }),
            () => postJson(API_ENDPOINTS.STUDENT_LOGIN, { username: loginId, password })
          ]
        : [
            () => postJson(API_ENDPOINTS.STUDENT_LOGIN, { username: loginId, password }),
            () => postJson(API_ENDPOINTS.ADMIN_LOGIN, { email: loginId, password }),
            () => postJson(API_ENDPOINTS.PARENT_LOGIN, { email: loginId, password })
          ];

      const data = await tryLogin(attempts);
      await routeUser(data);
    } catch (err) {
      setError('Invalid username/email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-10">
      <div className="bg-white border border-slate-200 shadow-lg rounded-lg w-full max-w-md p-7">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-blue-950">Creative Town Login</h1>
          <p className="text-slate-600 mt-2">Sign in with your school account.</p>
        </div>

        {new URLSearchParams(window.location.search).get('reason') === 'session-expired' && !error && (
          <p className="mb-4 text-sm text-slate-700" role="status">Please sign in again to continue.</p>
        )}

        {error && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-slate-800">Email or username</label>
            <input
              type="text"
              className="w-full p-3 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="name@example.com or username"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-slate-800">Password</label>
            <input
              type="password"
              className="w-full p-3 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-950 text-white py-3 rounded font-semibold hover:bg-blue-800 disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
